import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { filterMessages } from 'components/Filters/filterMessages';
import { HOTELS_TYPES } from 'components/FiltersPopovers/PropertyTypeFilterPopover';
import { TIERS } from 'components/SortBy/constants';
import { getFilterKey, getSortByLabels } from 'components/SortBy/helpers';
import { useFiltersForm } from 'hooks/useFiltersForm';
import { getCurrencyCode } from 'modules/meta/selectors';
import { searchParamToFacet } from 'modules/search/constants';
import { getListOfValues } from 'modules/search/selectors';
import { FilterUrlParams } from 'modules/search/types';
import { prop } from 'ramda';
import Settings from 'Settings';
import { getFacetLabel } from 'utils/filters';
import { HorizontalScroll } from '@daedalus/atlas/helpers/HorizontalScroll';
import { Icon } from '@daedalus/atlas/Icon';
import { Pill } from '@daedalus/atlas/Pill';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { getReviewSentimentKey, reviewSentimentMessages } from '@daedalus/core/src/hotel/reviewSentiments';
import { getEnabledCurrencies } from '@daedalus/core/src/localization/business/currency';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
type Props = Pick<ReturnType<typeof useFiltersForm>, 'getFilters' | 'toggleMultiFilter' | 'toggleFilters'>;
interface PillProps extends Props {
  isActive: boolean;
  className?: string;
}
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "wy9seos",
  propsAsIs: false
});
export const FilterOverlaySummary: React.FC<Props> = ({
  getFilters,
  toggleMultiFilter,
  toggleFilters
}) => {
  return <Wrapper>
      <HorizontalScroll columnGap="s300" rowGap="s300">
        <FacilityPills getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <MealPills getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <ThemePills getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <PropertyTypePills getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <FreeCancellationPill getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <ReviewScorePill getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <PricePill getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <StarRatingsPill getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
        <SortPill getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} isActive />
      </HorizontalScroll>
    </Wrapper>;
};
export const FacilityPills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const listOfValues = useSelector(getListOfValues);
  const {
    facilities
  } = getFilters();
  const getLabel = getFacetLabel('Facility', listOfValues);
  const facilitiesKey = searchParamToFacet.facilities.param;
  if (facilities) {
    return <>
        {facilities.map(facetId => {
        return <Pill className={className} key={`Facility:${facetId}`} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => toggleMultiFilter(facilitiesKey as keyof FilterUrlParams, facetId)}>
              {getLabel(facetId)}
            </Pill>;
      })}
      </>;
  }
  return null;
};
export const ThemePills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {
    themes
  } = getFilters();
  const listOfValues = useSelector(getListOfValues);
  const getLabel = getFacetLabel('Theme', listOfValues);
  const themesKey = searchParamToFacet.themes.param;
  if (themes) {
    return <>
        {themes.map(themeId => {
        return <Pill className={className} key={`Theme:${themeId}`} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => toggleMultiFilter(themesKey as keyof FilterUrlParams, themeId)}>
              {getLabel(themeId)}
            </Pill>;
      })}
      </>;
  }
  return null;
};
export const PropertyTypePills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {
    propertyTypes
  } = getFilters();
  const listOfValues = useSelector(getListOfValues);
  const getLabel = getFacetLabel('PropertyType', listOfValues);
  const propertyTypesKey = searchParamToFacet.propertyTypes.param;
  const propertyTypesList = propertyTypes ? [propertyTypes].flat() : [];
  const getIconStart = propertyId => HOTELS_TYPES.has(propertyId) ? 'Building' : 'House';

  // hotels property types should be displayed first
  const propertyTypesListSorted = [...propertyTypesList].sort(a => HOTELS_TYPES.has(a) ? -1 : 0);
  if (propertyTypesListSorted.length) {
    return <>
        {propertyTypesListSorted.map(propertyId => {
        return <Pill className={className} key={`PropertyType:${propertyId}`} isSelected={isActive} iconStart={<Icon name={getIconStart(propertyId)} />} iconEnd={<Icon name={'Close'} />} onClick={() => toggleMultiFilter(propertyTypesKey as keyof FilterUrlParams, propertyId)}>
              {getLabel(propertyId)}
            </Pill>;
      })}
      </>;
  }
  return null;
};
export const StarRatingsPill: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {
    starRatings
  } = getFilters();
  const starRatingKey = searchParamToFacet.starRatings.param;
  if (starRatings) {
    return <>
        {starRatings.map(starRating => {
        return <Pill className={className} key={`StarRatings:${starRating}`} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => toggleMultiFilter(starRatingKey as keyof FilterUrlParams, starRating)}>
              <FormattedMessageWrapper id="filters.starRating" defaultMessage="{starRating, plural, one {1 star} other {# stars}}" values={{
            starRating
          }} />
            </Pill>;
      })}
      </>;
  }
  return null;
};
export const FreeCancellationPill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const {
    formatMessage
  } = useIntl();
  const filters = getFilters();
  const {
    freeCancellation
  } = filters;
  const handleSingleFilterChange = useCallback((value: FilterUrlParams) => {
    toggleFilters({
      ...filters,
      ...value
    });
  }, [toggleFilters, filters]);
  return freeCancellation ? <Pill className={className} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => handleSingleFilterChange({
    freeCancellation: undefined
  })}>
      {formatMessage(filterMessages.freeCancellationTitle)}
    </Pill> : null;
};
export const ReviewScorePill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const {
    formatMessage
  } = useIntl();
  const filters = getFilters();
  const {
    reviewScore
  } = filters;
  const handleSingleFilterChange = useCallback((value: FilterUrlParams) => {
    toggleFilters({
      ...filters,
      ...value
    });
  }, [toggleFilters, filters]);
  if (reviewScore) {
    const sentimentMessageKey = getReviewSentimentKey(Number(reviewScore));
    const translatedSentiment = sentimentMessageKey ? reviewSentimentMessages[sentimentMessageKey] : null;
    return <Pill className={className} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => handleSingleFilterChange({
      reviewScore: undefined
    })}>
        {`${formatMessage(translatedSentiment)} ${reviewScore}+`}
      </Pill>;
  }
  return null;
};
const PricePill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const currencyCode = useSelector(getCurrencyCode);
  const filters = getFilters();
  const {
    priceMin,
    priceMax
  } = filters;
  const handleSingleFilterChange = useCallback((value: FilterUrlParams) => {
    toggleFilters({
      ...filters,
      ...value
    });
  }, [toggleFilters, filters]);
  if (priceMin || priceMax) {
    const enabledCurrencies = getEnabledCurrencies(Settings.get('ENABLED_CURRENCIES'));
    const currency = enabledCurrencies.find(currency => currency.code === currencyCode);
    const currencySymbol = prop('symbol', currency);
    return <Pill className={className} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => handleSingleFilterChange({
      priceMin: undefined,
      priceMax: undefined
    })}>
        {currencySymbol}
        {priceMin} - {currencySymbol}
        {priceMax}
      </Pill>;
  }
  return null;
};
const SortPill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const {
    formatMessage
  } = useIntl();
  const {
    brandIsVio
  } = useBrand();
  const sortLabels = getSortByLabels();
  const filters = getFilters();
  const {
    sortField,
    sortOrder
  } = filters;
  const handleSingleFilterChange = useCallback((value: FilterUrlParams) => {
    toggleFilters({
      ...filters,
      ...value
    });
  }, [toggleFilters, filters]);
  if (sortField && sortOrder) {
    const tier = brandIsVio ? TIERS.Gems : TIERS.PrivateDeal;
    const label = `${formatMessage({
      id: 'sortBy',
      defaultMessage: 'Sort by'
    })}: ${formatMessage(sortLabels[getFilterKey(sortField, sortOrder)?.key], {
      tier
    })}`;
    return <Pill className={className} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => handleSingleFilterChange({
      sortField: undefined,
      sortOrder: undefined
    })}>
        {label}
      </Pill>;
  }
  return null;
};
const MealPills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {
    formatMessage
  } = useIntl();
  const {
    amenities
  } = getFilters();
  if (!amenities || amenities.length === 0) return null;
  return <>
      {amenities.map(mealId => {
      return <Pill className={className} key={`Amenities:${mealId}`} isSelected={isActive} iconEnd={<Icon name={'Close'} />} onClick={() => toggleMultiFilter('amenities', mealId)}>
            {formatMessage(filterMessages[mealId])}
          </Pill>;
    })}
    </>;
};

require("./FilterOverlaySummary.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./FilterOverlaySummary.tsx");